/**
 * Finishing the upload of a recording that is no longer being made.
 *
 * Uploading was tied to recording: a `ChunkUploader` only ever existed inside
 * an `AttemptRecorder`, which existed only while the exam page was mounted.
 * That left one gap, and it was the worst possible one.
 *
 * When the student submits, the exam page runs a bounded 8-second final drain
 * and then unmounts, disposing the recorders and stopping their uploaders. The
 * submitted page then counted what was left in IndexedDB and told the student
 * "Uploading your recording — N segments left. Keep this page open until it
 * finishes." **Nothing was uploading.** The count could not move, the page
 * could never finish, and a student who did as they were told and waited was
 * watching a frozen number. When they eventually gave up and closed the tab,
 * the footage was gone.
 *
 * That is not a rare edge: on a slow connection the queue builds through the
 * whole exam, so the fuller the backlog — the more footage there is to lose —
 * the more certainly it was lost. One real attempt reached submit with 29
 * segments pending and uploaded none of them.
 *
 * This hook exists so the drain can outlive the recorder. It owns uploaders
 * for the sources the laptop is responsible for, keeps them running for as
 * long as it is mounted, and reports honest combined progress.
 *
 * It never produces chunks — there is no MediaRecorder here, no camera, no
 * permission. It only empties a queue that already exists in IndexedDB, which
 * is also why it is safe to mount on a page the student may reload: the queue
 * is durable and picking it back up is the point.
 */
import { useEffect, useMemo, useState } from "react";
import type { RecordingSource } from "@exam/shared";
import { ChunkUploader, type UploaderStatus } from "./uploader";
import { combineUploadStatus } from "./uploadHealth";

export interface UploadDrain {
  /** Combined status across every source being drained. */
  status: UploaderStatus;
  /** Everything this device had to send is on the server. */
  done: boolean;
  /**
   * Nothing left to try, but some of it was refused outright and will never
   * land.
   *
   * A distinct state from `done` on purpose. `pending` deliberately excludes
   * refused chunks — they must not be retried, or the drain loops on them
   * forever — which means "pending is zero" is NOT the same as "it all got
   * there". The first version of this conflated the two and showed a green
   * "All recording uploaded from this laptop" over six segments the server
   * had just rejected. Telling a student their footage is safe when it is not
   * is worse than telling them nothing.
   */
  blocked: boolean;
  /**
   * How much of the backlog has gone, 0..1, measured from the high-water mark
   * rather than from what is left.
   *
   * A bar computed from `pending` alone runs backwards whenever a late chunk
   * is queued, and on a slow link the student would watch it slip while being
   * told it was progressing.
   */
  progress: number;
}

export function useUploadDrain({
  attemptId,
  sources,
  deviceToken,
  enabled = true,
}: {
  attemptId: string | null;
  sources: readonly RecordingSource[];
  deviceToken?: string;
  enabled?: boolean;
}): UploadDrain {
  const [bySource, setBySource] = useState<Record<string, UploaderStatus>>({});
  /** Largest backlog seen, so the progress bar only ever moves forwards. */
  const [peak, setPeak] = useState(0);

  // Stable across renders so the effect below does not tear down and rebuild
  // every uploader when the caller passes a fresh array literal.
  const key = sources.join(",");

  useEffect(() => {
    if (!attemptId || !enabled) return;

    const list = key.split(",").filter(Boolean) as RecordingSource[];
    const uploaders = list.map((source) => {
      const uploader = new ChunkUploader({
        attemptId,
        source,
        deviceToken,
        onStatus: (status) =>
          setBySource((prev) => ({ ...prev, [source]: status })),
      });
      uploader.start();
      return uploader;
    });

    return () => {
      // Stops the loops; anything still queued stays in IndexedDB and is
      // picked up again if the student comes back to this page.
      for (const uploader of uploaders) uploader.stop();
    };
  }, [attemptId, key, deviceToken, enabled]);

  const status = useMemo(
    // Returns a fully zeroed status for an empty list, so no fallback here.
    () => combineUploadStatus(Object.values(bySource)),
    [bySource],
  );

  const outstanding = status.pending + status.uploaded;
  useEffect(() => {
    setPeak((current) => Math.max(current, outstanding));
  }, [outstanding]);

  return {
    status,
    // Only after something has actually reported. Before the first status
    // arrives every count is zero, and treating that as "done" would flash a
    // green tick over a backlog that has not been looked at yet.
    done:
      Object.keys(bySource).length > 0 &&
      status.pending === 0 &&
      status.refused === 0,
    blocked: status.pending === 0 && status.refused > 0,
    progress: peak === 0 ? 0 : Math.min(1, status.uploaded / peak),
  };
}
